// src/api.ts

// const HOST = "http://localhost:5010"

/**
 * Map Snowflake data types to JavaScript/React data types and formatting info
 */
export interface ColumnTypeInfo {
  jsType: 'number' | 'string' | 'boolean' | 'date' | 'object';
  sortType: 'alphanumeric' | 'numeric' | 'datetime' | 'basic';
  formatFunction: (value: any) => string;
  alignment?: 'left' | 'right' | 'center';
}

export const getColumnTypeInfo = (snowflakeType: string): ColumnTypeInfo => {
  const upperType = snowflakeType.toUpperCase();

  switch (upperType) {
    case 'DECIMAL':
    case 'FLOAT':
    case 'DOUBLE':
      return {
        jsType: 'number',
        sortType: 'numeric',
        alignment: 'right',
        formatFunction: (value) => {
          // Return original value without forced decimal places
          return String(value || '');
        }
      };

    case 'TINYINT':
    case 'SMALLINT':
    case 'INTEGER':
    case 'BIGINT':
      return {
        jsType: 'number',
        sortType: 'numeric',
        alignment: 'right',
        formatFunction: (value) => {
          // Return original value without unnecessary parsing
          return String(value || '');
        }
      };

    case 'DATE':
      return {
        jsType: 'date',
        sortType: 'datetime',
        alignment: 'center',
        formatFunction: (value) => {
          if (!value) return '';
          // Keep dates as-is, can be enhanced later
          return String(value);
        }
      };

    case 'TIME':
    case 'TIMESTAMP':
    case 'TIME_TZ':
    case 'TIMESTAMP_TZ':
    case 'TIMESTAMP_LTZ':
    case 'TIMESTAMP_NTZ':
      return {
        jsType: 'date',
        sortType: 'datetime',
        alignment: 'center',
        formatFunction: (value) => {
          if (!value) return '';
          // Can add more sophisticated time formatting here
          return String(value);
        }
      };

    case 'BOOLEAN':
      return {
        jsType: 'boolean',
        sortType: 'basic',
        alignment: 'center',
        formatFunction: (value) => {
          if (value === null || value === undefined) return '';

          // Handle string-based boolean values from API
          if (typeof value === 'string') {
            const lowerValue = value.toLowerCase();
            if (lowerValue === 'true') return 'True';
            if (lowerValue === 'false') return 'False';
          }

          // Handle actual boolean values
          if (typeof value === 'boolean') {
            return value ? 'True' : 'False';
          }

          // Fallback for other truthy/falsy values
          return value ? 'True' : 'False';
        }
      };

    case 'VARCHAR':
    case 'CHAR':
    case 'LONGVARCHAR':
    default:
      return {
        jsType: 'string',
        sortType: 'alphanumeric',
        alignment: 'left',
        formatFunction: (value) => String(value || '')
      };

    case 'ARRAY':
    case 'OBJECT':
    case 'VARIANT':
      return {
        jsType: 'object',
        sortType: 'basic',
        alignment: 'left',
        formatFunction: (value) => {
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          return String(value || '');
        }
      };
  }
};

/**
 * @deprecated Use table-level formatting instead of preprocessing data
 * Legacy function kept for backward compatibility
 */
export const formatValueByType = (value: any, columnType?: string): any => {
  if (!columnType) return value;
  if (value === null || value === undefined) return value;

  const typeInfo = getColumnTypeInfo(columnType);
  return typeInfo.formatFunction(value);
};

/**
 * Legacy formatValue function (kept for backward compatibility and fallback)
 * @deprecated Use formatValueByType with metadata when available
 */
export const formatValue = (value: any) => {
  // Handle numbers with commas by removing commas
  if (typeof value === 'string' && value.includes(',')) {
    value = value.replace(/,/g, '');
  }

  // Handle datetime values in "YYYY-MM-DD HH:mm:ss.SSSSSS" format
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d+$/.test(value)
  ) {
    return value.split('.')[0]; // Trim milliseconds
  }

  // Handle standard date values **before** treating it as a number
  const date = new Date(value);
  if (
    typeof value === 'string' &&
    !isNaN(date.getTime()) &&
    value.includes('-')
  ) {
    if (value.includes('T')) {
      return value.split('.')[0]; // Trim milliseconds from ISO format
    } else {
      return date.toLocaleDateString('en-CA'); // 'YYYY-MM-DD'
    }
  }

  const parsedValue = parseFloat(value);

  if (!isNaN(parsedValue) && value.toString().trim() !== '') {
    // Example: value = "1124.00" -> returns "1124"
    if (value.includes('.') && /^-?\d+(\.\d*0+)?$/.test(value)) {
      return parsedValue.toString();
    }

    // Example: value = "1124.190294" -> returns "1124.19"
    if (!Number.isInteger(parsedValue)) {
      return parsedValue.toFixed(2); // Format with 2 decimal places
    }

    return parsedValue.toString();
  }

  return value; // Return original value if not a date or number
};

/**
 * @deprecated Formatting moved to table-level rendering for better performance
 * Now returns raw data - formatting handled by TableComponent
 */
export const formatSqlResult = (sqlResult: any[], metadata?: any) => {
  // Return raw data without any preprocessing
  // TableComponent will handle formatting during rendering
  return sqlResult;
};

export const sanitizeString = (inputString: string) => {
  return inputString.replace(/['"/\\;]/g, '');
};

export const processSQLQuery = (
  sql_query: string, // SQL query string (optional)
  addMessage: any, // Function to add message
  prompt_id: any,
  semantic_model?: string, // Add semantic model parameter
  account_name?: string    // Add account name parameter
) => {
  addMessage({
    type: 'sql_query',
    columns: null,
    data: null,
    sql: sql_query || '', // Use the provided SQL query or fallback to an empty string
    prompt_id: prompt_id,
    semantic_model: semantic_model,  // Add semantic model for feedback
    account_name: account_name,      // Add account name for feedback
    showFeedback: true  // Enable feedback for SQL queries
  });
};

export const formatSQLQuery = (message: any) => {
  const sqlQuery = message.sql_statement.join(' ');

  if (message.sql_statement) {
    return sqlQuery;
  }
  return message.sql_statement;
};

interface SQLResult {
  dynamicColumns: { Header: string; accessor: string }[];
  formatedata: { [key: string]: any }[];
}

export const handleSQLResult = (
  sql_result: any,
  setColumns: React.Dispatch<React.SetStateAction<any>>,
  metadata?: any
): SQLResult | null => {
  if (sql_result && sql_result.length > 0) {
    const keys = Object.keys(sql_result[0]);
    const dynamicColumns = keys.map((key) => ({
      Header: key,
      accessor: key,
    }));

    setColumns(dynamicColumns);
    // Pass raw data to TableComponent - no preprocessing formatting
    const formatedata = sql_result; // Raw data, formatting handled at table level

    return { dynamicColumns, formatedata };
  }
  return null;
};

export const processSQLResult = (
  sql_result: any, // Raw SQL result
  sql_query: string, // SQL query string (optional)
  setColumns: React.Dispatch<React.SetStateAction<any>>, // Function to set dynamic columns
  addMessage: any, // Function to add message
  meta?: { prompt_id?: string; semantic_model?: string; account_name?: string },
  metadata?: any // Column metadata from API response
) => {
  const res: SQLResult | null = handleSQLResult(sql_result, setColumns, metadata);
  // Destructure the dynamicColumns and formatedata from the result, if available
  const dynamicColumns = res?.dynamicColumns;
  const formatedata = res?.formatedata;

  // If dynamicColumns are available, add an SQL result message

  addMessage({
    type: 'sql_result',
    columns: dynamicColumns,
    data: formatedata,
    columnMetadata: metadata?.column_metadata, // Include column metadata for table formatting
    sql: sql_query || '', // Use the provided SQL query or fallback to an empty string
    prompt_id: meta?.prompt_id,
    semantic_model: meta?.semantic_model,
    account_name: meta?.account_name,
    showFeedback: true  // Enable feedback for SQL results
  });
};